import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Crown, Check, Loader2 } from 'lucide-react';
import { redirectToCheckout, useDisplayPrice } from '../../lib/stripe';
import { products } from '../../stripe-config';

function PremiumFeatureList() {
  return (
    <div className="space-y-3">
      <div className="flex items-start">
        <Check className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
        <p className="text-gray-700 dark:text-gray-300">Unlimited focus sessions and cycles</p>
      </div>
      <div className="flex items-start">
        <Check className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
        <p className="text-gray-700 dark:text-gray-300">Custom audio for focus, break, and completion sounds</p>
      </div>
      <div className="flex items-start">
        <Check className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
        <p className="text-gray-700 dark:text-gray-300">Save and load custom timer presets</p>
      </div>
      <div className="flex items-start">
        <Check className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
        <p className="text-gray-700 dark:text-gray-300">Priority support and future updates</p>
      </div>
    </div>
  );
}

interface PremiumModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function PremiumModal({ isOpen, onClose }: PremiumModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const displayPrice = useDisplayPrice();

  const handleCheckout = async () => {
    try {
      setError(null);
      setIsLoading(true);
      await redirectToCheckout(products.pomoTimer.priceId);
    } catch (error) {
      console.error('Failed to start checkout:', error);
      setError(
        error instanceof Error ? error.message : 'Failed to start checkout',
      );
      setIsLoading(false);
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6">
          <div className="flex justify-between items-center mb-6">
            <Dialog.Title className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <Crown className="w-6 h-6 text-amber-500 mr-2" />
              Upgrade to Premium
            </Dialog.Title>
            <Dialog.Close className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
              <X className="w-5 h-5" />
            </Dialog.Close>
          </div>

          <div className="mb-6">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              ${displayPrice ?? import.meta.env.VITE_PRICE_LIFETIME}
              <span className="text-base font-normal text-gray-500 dark:text-gray-400"> one-time payment</span>
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Unlock all premium features and support our development.
            </p>
            <PremiumFeatureList />
          </div>

          {error && (
            <p className="mb-4 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded p-3">
              {error}
            </p>
          )}

          <button
            onClick={handleCheckout}
            disabled={isLoading}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              'Continue to Checkout'
            )}
          </button>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}